import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Zap, Check, Loader2, CreditCard, Package, 
  Crown, Star, Award, Sparkles, AlertCircle,
  Plus, Minus, Calendar, Receipt, Settings,
  XCircle, RefreshCw, ExternalLink, BarChart3,
  Video, Mic, Brain, Image, ChevronDown, ChevronUp
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getApiBaseUrl } from '@/config/api';

const API = getApiBaseUrl();

// Plan icons and colors
const PLAN_CONFIG = {
  ambassador: {
    icon: Star,
    color: "zinc",
    bgClass: "bg-zinc-500/10",
    borderClass: "border-zinc-500/30",
    textClass: "text-zinc-400"
  },
  apprentice: {
    icon: Sparkles,
    color: "blue",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/30",
    textClass: "text-blue-400"
  },
  director: {
    icon: Award,
    color: "purple",
    bgClass: "bg-purple-500/10",
    borderClass: "border-purple-500/30",
    textClass: "text-purple-400"
  },
  studio: {
    icon: Crown,
    color: "amber",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/30",
    textClass: "text-amber-400"
  }
};

// Usage type display configuration
const USAGE_TYPE_CONFIG = {
  animation_generation: {
    name: "Animation Generation",
    description: "Kling AI video generation",
    icon: Video,
    color: "text-purple-400",
    bgColor: "bg-purple-500/20"
  },
  lipsync_sync: {
    name: "Lip Sync (Sync)",
    description: "ADR lip sync on animated videos",
    icon: Mic,
    color: "text-blue-400",
    bgColor: "bg-blue-500/20"
  },
  lipsync_hedra: {
    name: "Dialogue (Hedra)",
    description: "Lip sync + animation for dialogue",
    icon: Mic,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/20"
  },
  ai_analysis: {
    name: "AI Analysis",
    description: "Script analysis & prompt improvement",
    icon: Brain,
    color: "text-green-400",
    bgColor: "bg-green-500/20"
  },
  pose_detection: {
    name: "Pose Detection",
    description: "Character pose analysis",
    icon: Image,
    color: "text-amber-400",
    bgColor: "bg-amber-500/20"
  },
  lipsync: {
    name: "Lip Sync",
    description: "Video lip synchronization",
    icon: Mic,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/20"
  }
};

export default function BillingSection() {
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sparkData, setSparkData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [checkingOut, setCheckingOut] = useState(false);
  
  // Usage breakdown
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  
  // Spark pack purchase
  const [showSparkPackModal, setShowSparkPackModal] = useState(false);
  const [sparkPackAmount, setSparkPackAmount] = useState(2500);
  const [sparkPackRecurring, setSparkPackRecurring] = useState(false);
  const [sparkPackPrice, setSparkPackPrice] = useState(10);
  
  // Subscription management
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  
  // Payment success handling
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for payment/subscription success
    const paymentSuccess = searchParams.get("payment_success");
    const subscriptionSuccess = searchParams.get("subscription_success");
    const sessionId = searchParams.get("session_id");
    
    if ((paymentSuccess === "true" || subscriptionSuccess === "true") && sessionId) {
      handlePaymentSuccess(sessionId, subscriptionSuccess === "true");
    }
  }, []);

  const fetchData = async () => {
    try {
      const [sparksRes, plansRes, subRes, usageRes] = await Promise.all([
        axios.get(`${API}/api/billing/my-sparks`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/billing/plans`),
        axios.get(`${API}/api/billing/my-subscription`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/billing/usage/summary`, { headers: getAuthHeaders() })
      ]);
      setSparkData(sparksRes.data);
      setPlans(plansRes.data.plans);
      setSubscriptionData(subRes.data);
      setUsageSummary(usageRes.data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
      toast.error("Failed to load billing information");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageHistory = async () => {
    setLoadingUsage(true);
    try {
      const response = await axios.get(`${API}/api/billing/usage/history?limit=50`, {
        headers: getAuthHeaders()
      });
      setUsageHistory(response.data.usage || []);
    } catch (error) {
      console.error("Failed to fetch usage history:", error);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handlePaymentSuccess = async (sessionId, isSubscription) => {
    setProcessingPayment(true);
    try {
      const response = await axios.get(`${API}/api/billing/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.status === "complete" || response.data.payment_status === "paid") {
        if (isSubscription) {
          toast.success("Subscription activated! Welcome to your new plan.");
        } else {
          toast.success("Payment successful! Your sparks have been added.");
        }
        fetchData();
      }
    } catch (error) {
      console.error("Failed to process payment:", error);
      toast.error("Failed to verify payment. Please contact support.");
    } finally {
      setProcessingPayment(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === "ambassador") {
      toast.info("You're on the free Ambassador plan!");
      return;
    }
    
    setCheckingOut(true);
    setSelectedPlan(planId);
    
    try {
      const response = await axios.post(
        `${API}/api/billing/checkout/subscription`,
        { plan: planId, billing_cycle: billingCycle },
        { headers: getAuthHeaders() }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
      setCheckingOut(false);
      setSelectedPlan(null);
    }
  };

  const handleChangePlan = async (newPlanId) => {
    if (!subscriptionData?.has_subscription) {
      // No active subscription, create new one
      handleSubscribe(newPlanId);
      return;
    }
    
    setCheckingOut(true);
    setSelectedPlan(newPlanId);
    
    try {
      await axios.post(
        `${API}/api/billing/subscription/change-plan`,
        { new_plan: newPlanId, new_billing_cycle: billingCycle, prorate: true },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Plan changed successfully! Charges will be prorated.");
      fetchData();
    } catch (error) {
      console.error("Failed to change plan:", error);
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setCheckingOut(false);
      setSelectedPlan(null);
    }
  };

  const handleCancelSubscription = async (immediately = false) => {
    setCancelling(true);
    
    try {
      await axios.post(
        `${API}/api/billing/subscription/cancel`,
        { at_period_end: !immediately },
        { headers: getAuthHeaders() }
      );
      
      if (immediately) {
        toast.success("Subscription cancelled. You've been moved to the free plan.");
      } else {
        toast.success("Subscription will cancel at the end of your billing period.");
      }
      
      setShowCancelModal(false);
      fetchData();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    
    try {
      await axios.post(
        `${API}/api/billing/subscription/reactivate`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success("Subscription reactivated!");
      fetchData();
    } catch (error) {
      console.error("Failed to reactivate subscription:", error);
      toast.error("Failed to reactivate subscription");
    } finally {
      setReactivating(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const response = await axios.get(
        `${API}/api/billing/subscription/portal`,
        { headers: getAuthHeaders() }
      );
      
      window.open(response.data.portal_url, '_blank');
    } catch (error) {
      console.error("Failed to open billing portal:", error);
      toast.error("Failed to open billing portal");
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await axios.get(
        `${API}/api/billing/subscription/invoices?limit=20`,
        { headers: getAuthHeaders() }
      );
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleBuySparkPack = async () => {
    setCheckingOut(true);
    
    try {
      const response = await axios.post(
        `${API}/api/billing/checkout/spark-pack?sparks=${sparkPackAmount}&recurring=${sparkPackRecurring}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Failed to create spark pack checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  const updateSparkPackAmount = (delta) => {
    const newAmount = sparkPackAmount + delta;
    if (newAmount >= 2500 && newAmount <= 100000) {
      setSparkPackAmount(newAmount);
      setSparkPackPrice((newAmount / 2500) * 10);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (processingPayment) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-400" />
        <p className="text-zinc-400">Processing your payment...</p>
      </div>
    );
  }

  const currentPlanConfig = PLAN_CONFIG[sparkData?.subscription_plan] || PLAN_CONFIG.ambassador;
  const CurrentPlanIcon = currentPlanConfig.icon;
  const hasActiveSubscription = subscriptionData?.has_subscription && subscriptionData?.subscription_status === "active";
  const isPendingCancellation = subscriptionData?.cancel_at_period_end;

  return (
    <div className="space-y-8">
      {/* Current Spark Balance */}
      <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Zap className="w-8 h-8 text-indigo-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Your Spark Balance</p>
                <p className="text-4xl font-bold text-white" data-testid="spark-balance-display">
                  {sparkData?.spark_balance?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline" 
                className={`${currentPlanConfig.bgClass} ${currentPlanConfig.textClass} ${currentPlanConfig.borderClass} px-3 py-1`}
              >
                <CurrentPlanIcon className="w-3 h-3 mr-1" />
                {sparkData?.plan_name} Plan
              </Badge>
              
              <Button
                onClick={() => setShowSparkPackModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="buy-sparks-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Buy Sparks
              </Button>
            </div>
          </div>
          
          {sparkData?.watermarked && (
            <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              Free plan exports include watermarks. Upgrade for clean exports!
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Management (for active subscribers) */}
      {hasActiveSubscription && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-400" />
              Subscription Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Subscription Info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Current Plan</span>
                  <span className="text-white font-medium">{subscriptionData?.plan_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Billing Cycle</span>
                  <span className="text-white capitalize">{subscriptionData?.billing_cycle}</span>
                </div>
                {subscriptionData?.subscription_end_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">
                      {isPendingCancellation ? "Cancels On" : "Next Billing"}
                    </span>
                    <span className={isPendingCancellation ? "text-amber-400" : "text-white"}>
                      {new Date(subscriptionData.subscription_end_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {subscriptionData?.upcoming_invoice && (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Next Invoice</span>
                    <span className="text-white">${subscriptionData.upcoming_invoice.amount_due}</span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="border-zinc-700 justify-start"
                  onClick={() => {
                    fetchInvoices();
                    setShowInvoicesModal(true);
                  }}
                  data-testid="view-invoices-btn"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  View Invoices
                </Button>
                
                {subscriptionData?.stripe_customer_id && (
                  <Button
                    variant="outline"
                    className="border-zinc-700 justify-start"
                    onClick={handleOpenBillingPortal}
                    data-testid="manage-payment-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Payment Methods
                    <ExternalLink className="w-3 h-3 ml-auto" />
                  </Button>
                )}
                
                {isPendingCancellation ? (
                  <Button
                    variant="outline"
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10 justify-start"
                    onClick={handleReactivateSubscription}
                    disabled={reactivating}
                    data-testid="reactivate-subscription-btn"
                  >
                    {reactivating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Reactivate Subscription
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 justify-start"
                    onClick={() => setShowCancelModal(true)}
                    data-testid="cancel-subscription-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
            
            {isPendingCancellation && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">
                    Your subscription is set to cancel on {new Date(subscriptionData.subscription_end_date).toLocaleDateString()}.
                    You can reactivate anytime before then.
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Spark Usage Breakdown */}
      {usageSummary && usageSummary.total_sparks_used > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                Spark Usage Breakdown
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                onClick={() => {
                  if (!showUsageDetails) {
                    fetchUsageHistory();
                  }
                  setShowUsageDetails(!showUsageDetails);
                }}
                data-testid="toggle-usage-details"
              >
                {showUsageDetails ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Show History
                  </>
                )}
              </Button>
            </div>
            <CardDescription className="text-zinc-400">
              Total sparks used: <span className="text-white font-medium">{usageSummary.total_sparks_used.toLocaleString()}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Usage by Type - Visual Breakdown */}
            <div className="space-y-4">
              {Object.entries(usageSummary.by_type || {}).map(([type, data]) => {
                const config = USAGE_TYPE_CONFIG[type] || {
                  name: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                  icon: Zap,
                  color: "text-zinc-400",
                  bgColor: "bg-zinc-500/20"
                };
                const IconComponent = config.icon;
                const percentage = usageSummary.total_sparks_used > 0 
                  ? Math.round((data.sparks_used / usageSummary.total_sparks_used) * 100)
                  : 0;
                
                return (
                  <div key={type} className="space-y-2" data-testid={`usage-type-${type}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                          <IconComponent className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{config.name}</p>
                          <p className="text-zinc-500 text-xs">{data.count} {data.count === 1 ? 'operation' : 'operations'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">{data.sparks_used.toLocaleString()}</p>
                        <p className="text-zinc-500 text-xs">{percentage}% of total</p>
                      </div>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-2 bg-zinc-800"
                    />
                  </div>
                );
              })}
            </div>

            {/* Detailed History (Expandable) */}
            {showUsageDetails && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <h4 className="text-white text-sm font-medium mb-4">Recent Activity</h4>
                {loadingUsage ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                  </div>
                ) : usageHistory.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-4">No usage history available</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {usageHistory.slice(0, 20).map((item, index) => {
                      const config = USAGE_TYPE_CONFIG[item.usage_type] || {
                        name: item.usage_type,
                        icon: Zap,
                        color: "text-zinc-400"
                      };
                      const IconComponent = config.icon;
                      
                      return (
                        <div 
                          key={item.id || index}
                          className="flex items-center justify-between py-2 px-3 bg-zinc-800/30 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <IconComponent className={`w-4 h-4 ${config.color}`} />
                            <div>
                              <p className="text-white text-sm">{config.name}</p>
                              <p className="text-zinc-500 text-xs">
                                {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                            -{item.sparks_used.toLocaleString()} sparks
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-zinc-500'}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
          className={`relative w-14 h-7 rounded-full transition-colors ${
            billingCycle === 'annual' ? 'bg-indigo-600' : 'bg-zinc-700'
          }`}
          data-testid="billing-cycle-toggle"
        >
          <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
            billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-1'
          }`} />
        </button>
        <span className={`text-sm ${billingCycle === 'annual' ? 'text-white' : 'text-zinc-500'}`}>
          Annual
          <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30">
            2 months free
          </Badge>
        </span>
      </div>

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const config = PLAN_CONFIG[plan.id] || PLAN_CONFIG.ambassador;
          const PlanIcon = config.icon;
          const isCurrentPlan = sparkData?.subscription_plan === plan.id;
          const price = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
          const monthlyEquivalent = billingCycle === 'annual' ? (plan.price_annual / 12).toFixed(0) : plan.price_monthly;
          
          return (
            <Card 
              key={plan.id}
              className={`relative ${
                isCurrentPlan 
                  ? 'border-indigo-500 bg-indigo-500/5' 
                  : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
              } transition-all`}
              data-testid={`plan-card-${plan.id}`}
            >
              {isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-indigo-600 text-white">Current Plan</Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 mx-auto rounded-full ${config.bgClass} flex items-center justify-center mb-2`}>
                  <PlanIcon className={`w-6 h-6 ${config.textClass}`} />
                </div>
                <CardTitle className="text-white">{plan.name}</CardTitle>
                <div className="mt-2">
                  {plan.price_monthly === 0 ? (
                    <span className="text-3xl font-bold text-white">Free</span>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-white">${monthlyEquivalent}</span>
                      <span className="text-zinc-500">/mo</span>
                      {billingCycle === 'annual' && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Billed ${price}/year
                        </p>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-400" />
                    <span className="text-zinc-300 text-sm">
                      {plan.sparks_monthly > 0 
                        ? `${plan.sparks_monthly.toLocaleString()} sparks/mo`
                        : `${plan.sparks_onetime.toLocaleString()} sparks (one-time)`
                      }
                    </span>
                  </div>
                  
                  {plan.rollover && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-zinc-400 text-sm">Unused sparks roll over</span>
                    </div>
                  )}
                  
                  {!plan.watermarked && plan.id !== 'ambassador' && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-zinc-400 text-sm">No watermarks</span>
                    </div>
                  )}
                  
                  {plan.watermarked && (
                    <div className="flex items-center gap-2 text-zinc-500">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">Watermarked exports</span>
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={() => hasActiveSubscription && !isCurrentPlan && plan.id !== 'ambassador' 
                    ? handleChangePlan(plan.id) 
                    : handleSubscribe(plan.id)
                  }
                  disabled={isCurrentPlan || checkingOut || plan.id === 'ambassador'}
                  className={`w-full ${
                    isCurrentPlan 
                      ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                      : plan.id === 'ambassador'
                      ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {checkingOut && selectedPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isCurrentPlan ? (
                    "Current Plan"
                  ) : plan.id === 'ambassador' ? (
                    "Free Forever"
                  ) : hasActiveSubscription ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Switch Plan
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Spark Packs Info */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-400" />
            Spark Packs
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Need extra sparks? Purchase spark packs to top up your balance anytime.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[2500, 5000, 10000].map((sparks) => (
              <Button
                key={sparks}
                variant="outline"
                className="border-zinc-700 hover:border-indigo-500/50 h-auto py-4 flex flex-col"
                onClick={() => {
                  setSparkPackAmount(sparks);
                  setSparkPackPrice((sparks / 2500) * 10);
                  setShowSparkPackModal(true);
                }}
                data-testid={`spark-pack-${sparks}`}
              >
                <Zap className="w-6 h-6 text-indigo-400 mb-2" />
                <span className="text-white font-bold">{sparks.toLocaleString()} Sparks</span>
                <span className="text-zinc-500">${(sparks / 2500) * 10}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Spark Pack Purchase Modal */}
      <Dialog open={showSparkPackModal} onOpenChange={setShowSparkPackModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-indigo-400" />
              Buy Spark Pack
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Purchase additional sparks. Spark packs never expire and stack with your plan.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Amount Selector */}
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => updateSparkPackAmount(-2500)}
                  disabled={sparkPackAmount <= 2500}
                  className="border-zinc-700"
                  data-testid="decrease-sparks"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">
                    {sparkPackAmount.toLocaleString()}
                  </p>
                  <p className="text-zinc-500 text-sm">sparks</p>
                </div>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => updateSparkPackAmount(2500)}
                  disabled={sparkPackAmount >= 100000}
                  className="border-zinc-700"
                  data-testid="increase-sparks"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="text-center">
                <span className="text-2xl font-bold text-indigo-400">${sparkPackPrice}</span>
                <span className="text-zinc-500 ml-1">USD</span>
              </div>
            </div>

            {/* Recurring Option */}
            <div 
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                sparkPackRecurring ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-zinc-800/50 border border-transparent'
              }`}
              onClick={() => setSparkPackRecurring(!sparkPackRecurring)}
            >
              <div>
                <p className="text-white font-medium">Make it recurring</p>
                <p className="text-zinc-500 text-sm">Get these sparks added monthly</p>
              </div>
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                sparkPackRecurring ? 'bg-indigo-600 border-indigo-600' : 'border-zinc-600'
              }`}>
                {sparkPackRecurring && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowSparkPackModal(false)}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBuySparkPack}
              disabled={checkingOut}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-buy-sparks"
            >
              {checkingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Purchase ${sparkPackPrice}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <AlertDialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <div className="space-y-4">
                <p>
                  We're sorry to see you go! Your subscription provides you with monthly sparks
                  and watermark-free exports.
                </p>
                <div className="bg-zinc-800/50 p-4 rounded-lg space-y-2">
                  <p className="text-white font-medium">What happens when you cancel:</p>
                  <ul className="text-sm space-y-1">
                    <li>• You'll keep access until {subscriptionData?.subscription_end_date ? new Date(subscriptionData.subscription_end_date).toLocaleDateString() : "end of billing period"}</li>
                    <li>• Your remaining sparks will stay in your account</li>
                    <li>• After cancellation, exports will have watermarks</li>
                    <li>• You can reactivate anytime before the end date</li>
                  </ul>
                </div>
                <p className="text-amber-400 text-sm">
                  Tip: Consider downgrading to our free Ambassador plan instead - you'll keep your projects!
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Keep Subscription
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCancelSubscription(false)}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel at Period End"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoices Modal */}
      <Dialog open={showInvoicesModal} onOpenChange={setShowInvoicesModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-indigo-400" />
              Invoice History
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {loadingInvoices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No invoices found
              </div>
            ) : (
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-400' : 
                      invoice.status === 'open' ? 'bg-amber-400' : 'bg-zinc-400'
                    }`} />
                    <div>
                      <p className="text-white font-medium">
                        ${invoice.amount_paid?.toFixed(2) || invoice.amount_due?.toFixed(2)}
                      </p>
                      <p className="text-zinc-500 text-sm">
                        {new Date(invoice.created).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      className={`${
                        invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        invoice.status === 'open' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}
                    >
                      {invoice.status}
                    </Badge>
                    {invoice.invoice_pdf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-indigo-400 hover:text-indigo-300"
                        onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
